import type { LucideIcon } from 'lucide-react';
import type React from 'react';
import type { IconType } from 'react-icons';
import {
    WORKFLOW_STAGE_ICONS,
    WORKFLOW_STAGE_LABEL_IDS,
} from '~/components/WorkflowStages';
import type { WorkflowStageId } from '~/domain/workflow/workflowStages';
import ContouringContent from '../WorkflowSidebar/ContouringStep';
import PredictivePlanningContent from '../WorkflowSidebar/DoseStep';
import ExportContent from '../WorkflowSidebar/ExportStep';
import RegistrationContent from '../WorkflowSidebar/RegistrationStep';
import ReviewContent from '../WorkflowSidebar/ReviewStep';
import SetupContent from '../WorkflowSidebar/SetupStep';
import SyntheticImagingContent from '../WorkflowSidebar/SyntheticImagingStep';

/**
 * Data prerequisites that a workflow step may require.
 * Each maps to a specific i18n disabled-reason message.
 */
export type StepRequirement = 'image' | 'secondaryImages' | 'structureSet';

export const STEP_REQUIREMENT_REASON_ID: Record<StepRequirement, string> = {
    image: 'workflow.step.disabled.noImage',
    secondaryImages: 'workflow.step.disabled.noSecondaryImages',
    structureSet: 'workflow.step.disabled.noStructureSet',
};

/**
 * A workflow step is a way of looking at the loaded context, not a place. Its
 * id is what the `step` search parameter carries; the route names the patient,
 * the session or the local files, and never the step.
 */
export interface WorkflowStep {
    id: WorkflowStepId;
    /** The workflow stage this step is; its name and glyph come from the stage. */
    stage: WorkflowStageId;
    labelId: string;
    icon: LucideIcon | IconType;
    content: React.ComponentType;
    /** Data that must be loaded before this step is available. */
    requires?: StepRequirement[];
}

export type WorkflowStepId =
    | 'setup'
    | 'synthetic-imaging'
    | 'registration'
    | 'contouring'
    | 'review'
    | 'predictive-planning'
    | 'export';

type StepDefinition = Omit<WorkflowStep, 'labelId' | 'icon'>;

function step(definition: StepDefinition): WorkflowStep {
    return {
        ...definition,
        labelId: WORKFLOW_STAGE_LABEL_IDS[definition.stage],
        icon: WORKFLOW_STAGE_ICONS[definition.stage],
    };
}

/** The steps the viewer implements, in workflow order; Adapt has no step of its own yet. */
export const WORKFLOW_STEPS: WorkflowStep[] = [
    step({
        id: 'setup',
        stage: 'setup',
        content: SetupContent,
    }),
    step({
        id: 'synthetic-imaging',
        stage: 'syntheticImaging',
        content: SyntheticImagingContent,
        requires: ['image'],
    }),
    step({
        id: 'registration',
        stage: 'imageRegistration',
        content: RegistrationContent,
        requires: ['image'],
    }),
    step({
        id: 'contouring',
        stage: 'contouring',
        content: ContouringContent,
        requires: ['image'],
    }),
    step({
        id: 'review',
        stage: 'review',
        content: ReviewContent,
        requires: ['structureSet'],
        // Every review control acts on the grid or refuses with a reason:
        // the comparison draws on the row that shows the reviewed set, the
        // player sweeps that row's slices, and the fusion pickers hide by
        // design.
    }),
    step({
        id: 'predictive-planning',
        stage: 'dosePlanning',
        content: PredictivePlanningContent,
        requires: ['structureSet'],
        // The grid draws the active dose on its structure set's row,
        // follows the sidebar's dose display controls, and hosts the DVH
        // chart and the dose statistics in a band under its rows.
    }),
    step({
        id: 'export',
        stage: 'export',
        content: ExportContent,
        requires: ['image'],
    }),
];

/**
 * The step every context falls back to. It requires nothing, so it is always
 * available — which is what makes "show this step as soon as it is reachable"
 * safe: there is somewhere to be in the meantime.
 */
export const DEFAULT_WORKFLOW_STEP = WORKFLOW_STEPS[0];

/** The step with this id, or undefined for a name no step answers to. */
export function workflowStepById(
    id: string | null | undefined
): WorkflowStep | undefined {
    return WORKFLOW_STEPS.find((step) => step.id === id);
}
